// Kleine Bausteine (DESIGN §3.17): Icon, Kbd, Spinner, Divider, VisuallyHidden, SkipLink,
// ScrollArea, StickyFooter.
import { type Icon as PhosphorIcon, type IconWeight } from '@phosphor-icons/react';
import { ScrollArea as RadixScrollArea, VisuallyHidden as RadixVisuallyHidden } from 'radix-ui';
import { type ComponentPropsWithRef, type ReactNode } from 'react';
import { cn } from './cn';
import { cva, type VariantProps } from './cva';

export type IconSize = 14 | 16 | 20 | 22 | 24;

export interface IconProps extends Omit<ComponentPropsWithRef<'svg'>, 'ref'> {
  icon: PhosphorIcon;
  size?: IconSize;
  /** Navigation `fill`, Inhalt `regular` (DESIGN §4.6). */
  weight?: IconWeight;
  /** Ohne Label dekorativ (`aria-hidden`). */
  label?: string;
}

/** Phosphor-Icon in den Größen des Systems; Farbe = Textfarbe des Trägers. */
export function Icon({
  icon: Glyph,
  size = 20,
  weight = 'regular',
  label,
  className,
  ...rest
}: IconProps) {
  return (
    <Glyph
      size={size}
      weight={weight}
      className={cn('shrink-0', className)}
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? 'img' : undefined}
      {...rest}
    />
  );
}

/** Tastenkappe 20 hoch (DESIGN §3.17). */
export function Kbd({ className, ...rest }: ComponentPropsWithRef<'kbd'>) {
  return (
    <kbd
      className={cn(
        'inline-flex h-5 min-w-5 items-center justify-center rounded-xs bg-tile px-1.5 font-sans text-small font-medium text-fg-secondary',
        className,
      )}
      {...rest}
    />
  );
}

const spinnerSize = { 14: 'size-3.5', 16: 'size-4', 20: 'size-5' } as const;

/** Spinner 16 (14 in Chips); dekorativ, der Träger setzt `aria-busy`. */
export function Spinner({
  size = 16,
  className,
}: {
  size?: keyof typeof spinnerSize;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      data-slot="spinner"
      className={cn('shrink-0 animate-spin opacity-70', spinnerSize[size], className)}
    >
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
      <path
        d="M14.5 8A6.5 6.5 0 0 0 8 1.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export interface DividerProps extends ComponentPropsWithRef<'hr'> {
  orientation?: 'horizontal' | 'vertical';
}

/** 1 px `line-subtle`; senkrecht `line`. */
export function Divider({ orientation = 'horizontal', className, ...rest }: DividerProps) {
  return orientation === 'vertical' ? (
    <span
      role="separator"
      aria-orientation="vertical"
      className={cn('inline-block w-px self-stretch bg-line', className)}
    />
  ) : (
    <hr className={cn('h-px w-full border-0 bg-line-subtle', className)} {...rest} />
  );
}

export const VisuallyHidden = RadixVisuallyHidden.Root;

/** „Skip to content“: sichtbar erst bei Fokus, oben links (`primary`). */
export function SkipLink({
  href = '#main',
  children = 'Skip to content',
}: {
  href?: string;
  children?: ReactNode;
}) {
  return (
    <a
      href={href}
      className="fixed top-2 left-2 z-(--bar-z-skip) -translate-y-20 rounded-full bg-primary px-4 py-2 text-button text-on-primary transition-transform duration-(--bar-dur-fast) focus-visible:translate-y-0"
    >
      {children}
    </a>
  );
}

export interface ScrollAreaProps extends ComponentPropsWithRef<typeof RadixScrollArea.Root> {
  /** Klassen für den scrollenden Inhalt (Viewport). */
  viewportClassName?: string;
  /** Beschriftung, wenn der Bereich selbst fokussierbar sein soll. */
  label?: string;
}

/** Radix ScrollArea: Balken 6 `fg-decor` 50 %, nur bei Hover sichtbar. */
export function ScrollArea({
  children,
  className,
  viewportClassName,
  label,
  ...rest
}: ScrollAreaProps) {
  return (
    <RadixScrollArea.Root
      type="hover"
      className={cn('relative overflow-hidden', className)}
      {...rest}
    >
      <RadixScrollArea.Viewport
        className={cn('size-full rounded-[inherit]', viewportClassName)}
        aria-label={label}
        tabIndex={label ? 0 : undefined}
      >
        {children}
      </RadixScrollArea.Viewport>
      <RadixScrollArea.Scrollbar
        orientation="vertical"
        className="flex w-1.5 touch-none p-px opacity-100 transition-opacity duration-(--bar-dur-fast) select-none data-[state=hidden]:opacity-0"
      >
        <RadixScrollArea.Thumb className="relative flex-1 rounded-full bg-fg-decor/50" />
      </RadixScrollArea.Scrollbar>
    </RadixScrollArea.Root>
  );
}

const stickyFooterVariants = cva(
  'sticky bottom-[var(--bar-bottom-offset,0px)] z-(--bar-z-sticky) flex items-center gap-3 rounded-t-xl bg-card px-4 py-3 shadow-2',
  {
    variants: { justify: { end: 'justify-end', between: 'justify-between' } },
    defaultVariants: { justify: 'end' },
  },
);

export type StickyFooterProps = ComponentPropsWithRef<'div'> &
  VariantProps<typeof stickyFooterVariants>;

/** Klebende Fußleiste (Scorecard, Formulare); sitzt über der Bottom-Nav (`--bar-bottom-offset` der Shell). */
export function StickyFooter({ justify, className, ...rest }: StickyFooterProps) {
  return <div className={stickyFooterVariants({ justify, className })} {...rest} />;
}
